import React, { useRef, useCallback, useEffect } from 'react';
import { linkUtils } from '../utils/linkUtils';

interface RichTextParagraphProps {
  content: string;
  clientId: string;
  onChange: (content: string) => void;
  onDelete: () => void;
  placeholder?: string;
}

export const RichTextParagraph: React.FC<RichTextParagraphProps> = ({
  content,
  clientId,
  onChange,
  onDelete,
  placeholder = "Enter paragraph text..."
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const textContent = element.textContent || '';
    
    // Convert URLs to links automatically
    const htmlWithLinks = linkUtils.textToHtmlWithLinks(textContent);
    
    // Only update if content has changed
    if (htmlWithLinks !== element.innerHTML) {
      element.innerHTML = htmlWithLinks;
      
      // Restore cursor position after HTML update
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const cursorOffset = range.startOffset;
        
        // Move cursor to end of the content
        const textLength = textContent.length;
        if (cursorOffset >= textLength) {
          const newRange = document.createRange();
          newRange.selectNodeContents(element);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
    }
    
    onChange(textContent);
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && e.currentTarget.textContent === '') {
      e.preventDefault();
      onDelete();
    }
  }, [onDelete]);

  // Set initial content only once
  useEffect(() => {
    if (ref.current && !ref.current.textContent) {
      const htmlWithLinks = linkUtils.textToHtmlWithLinks(content);
      ref.current.innerHTML = htmlWithLinks;
    }
  }, []);

  return (
    <div
      ref={ref}
      className="block-editor-rich-text__editable block-editor-block-list__block wp-block is-selected wp-block-paragraph rich-text paragraph-input"
      id={`block-${clientId}`}
      aria-label="Block: Paragraph"
      data-block={clientId}
      data-type="core/paragraph"
      data-title="Paragraph"
      contentEditable={true}
      data-wp-block-attribute-key="content"
      style={{
        whiteSpace: 'pre-wrap',
        minWidth: '1px',
        overflowWrap: 'break-word',
        lineBreak: 'after-white-space' as any,
        margin: '0',
        fontSize: '16px',
        lineHeight: '1.6em',
        minHeight: '1.6em',
        padding: '0em 0',
        outline: 'none',
        ...({
          WebkitNbspMode: 'space',
          WebkitUserModify: 'read-write'
        } as any)
      }}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      suppressContentEditableWarning={true}
      data-placeholder={placeholder}
    />
  );
};
